import { useState, useCallback, useEffect, useRef } from 'react';
import { X, CheckCircle, AlertCircle, Clock } from 'lucide-react';

type ToastType = 'success' | 'error' | 'warning';
interface Toast { id: number; type: ToastType; message: string; }

let _addToast: ((type: ToastType, message: string) => void) | null = null;

export function toast(type: ToastType, message: string) {
  _addToast?.(type, message);
}

const TOAST_STYLES: Record<ToastType, string> = {
  success: 'bg-emerald-600 text-white',
  error:   'bg-red-600 text-white',
  warning: 'bg-amber-50 text-amber-900 border border-amber-200',
};

function ToastIcon({ type }: { type: ToastType }) {
  if (type === 'success') return <CheckCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-white" />;
  if (type === 'warning') return <Clock className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-500" />;
  return <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />;
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const add = useCallback((type: ToastType, message: string) => {
    const id = ++nextId.current;
    setToasts(prev => [...prev, { id, type, message }]);
    // Warning toasts stay longer (6s) since they need to be read carefully.
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), type === 'warning' ? 6000 : 4000);
  }, []);

  useEffect(() => { _addToast = add; return () => { _addToast = null; }; }, [add]);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 sm:top-6 inset-x-0 z-[9999] flex flex-col items-center gap-2 pointer-events-none">
      {toasts.map(t => (
        <div key={t.id}
          className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg text-sm max-w-sm animate-in slide-in-from-top duration-200 ${TOAST_STYLES[t.type]}`}>
          <ToastIcon type={t.type} />
          <span className="flex-1">{t.message}</span>
          <button
            onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
            className="opacity-50 hover:opacity-100 mt-0.5"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}
